import { useState, useCallback, useEffect } from 'react';
import type { Word } from '../../types';
import { api } from '../../services/api';
import './StageCommon.css';
import './TransferStage.css';

/**
 * Stage 11: Transfer to GAMES CORE + Stage 12: D1 Upload.
 * Two-step final stage: transfer approved content to GAMES CORE table,
 * then upload to D1 for the live game.
 */

interface TransferStageProps {
  word: Word;
  onApprove: (fields: Record<string, unknown>) => void;
  saving: boolean;
  registerApprovePayload: (fn: (() => Record<string, unknown> | null) | null) => void;
}

interface TransferResult {
  recordId?: string;
  [key: string]: unknown;
}

export default function TransferStage({ word, onApprove, saving, registerApprovePayload }: TransferStageProps) {
  const [transferring, setTransferring] = useState<boolean>(false);
  const [transferred, setTransferred] = useState<boolean>(Boolean(word.transferred));
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploaded, setUploaded] = useState<boolean>(Boolean(word.transferred_to_d1));

  useEffect(() => {
    if (!registerApprovePayload) return;
    registerApprovePayload(() => null);
  }, [registerApprovePayload]);
  const [error, setError] = useState<string | null>(null);
  const [transferResult, setTransferResult] = useState<TransferResult | null>(null);
  const [uploadResult, setUploadResult] = useState<TransferResult | null>(null);

  const handleTransfer = useCallback(async () => {
    setTransferring(true);
    setError(null);
    try {
      const result = await api.post('/api/airtable/transfer', {
        wordId: word.id,
      }) as TransferResult;
      setTransferred(true);
      setTransferResult(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setTransferring(false);
    }
  }, [word.id]);

  const handleD1Upload = useCallback(async () => {
    setUploading(true);
    setError(null);
    try {
      const result = await api.post('/api/airtable/d1-upload', {
        wordId: word.id,
      }) as TransferResult;
      setUploaded(true);
      setUploadResult(result);
      // Mark word as fully complete
      onApprove({ transferred_to_d1: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  }, [word.id, onApprove]);

  return (
    <div className="stage-view">
      <div className="transfer-stage">
        <h2 className="transfer-title">Transfer & Upload</h2>
        <p className="transfer-subtitle">
          Move <strong>{word.word}</strong> to production.
        </p>

        {error && (
          <div className="transfer-error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {/* Step 1: Transfer to GAMES CORE */}
        <div className={`transfer-step ${transferred ? 'step-complete' : ''}`}>
          <div className="transfer-step-header">
            <div className="transfer-step-indicator">
              {transferred ? (
                <span className="step-check">&#10003;</span>
              ) : (
                <span className="step-number">1</span>
              )}
            </div>
            <div className="transfer-step-info">
              <h3 className="transfer-step-title">Transfer to GAMES CORE</h3>
              <p className="transfer-step-desc">
                Copy approved content from Game Word Content to the GAMES CORE table in Airtable.
              </p>
            </div>
          </div>

          {!transferred && (
            <button
              className="transfer-btn"
              onClick={handleTransfer}
              disabled={transferring}
            >
              {transferring ? 'Transferring...' : 'Transfer to GAMES CORE'}
            </button>
          )}

          {transferred && transferResult && (
            <div className="transfer-result">
              Transferred successfully
              {transferResult.recordId && (
                <span className="transfer-record-id">Record: {transferResult.recordId}</span>
              )}
            </div>
          )}
        </div>

        {/* Step 2: D1 Upload */}
        <div className={`transfer-step ${uploaded ? 'step-complete' : ''} ${!transferred ? 'step-locked' : ''}`}>
          <div className="transfer-step-header">
            <div className="transfer-step-indicator">
              {uploaded ? (
                <span className="step-check">&#10003;</span>
              ) : (
                <span className="step-number">2</span>
              )}
            </div>
            <div className="transfer-step-info">
              <h3 className="transfer-step-title">Upload to D1</h3>
              <p className="transfer-step-desc">
                Sync GAMES CORE content to the D1 database for the live game.
              </p>
            </div>
          </div>

          {transferred && !uploaded && (
            <button
              className="transfer-btn upload-btn"
              onClick={handleD1Upload}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload to D1'}
            </button>
          )}

          {!transferred && (
            <p className="transfer-locked-hint">Complete transfer first</p>
          )}

          {uploaded && uploadResult && (
            <div className="transfer-result upload-result">
              Uploaded to D1 successfully
            </div>
          )}
        </div>

        {/* Final status */}
        {uploaded && (
          <div className="transfer-complete">
            <div className="transfer-complete-icon">&#10003;</div>
            <h3 className="transfer-complete-title">{word.word} is live!</h3>
            <p className="transfer-complete-text">
              Scheduled for {word.game_date || 'unscheduled'}.
              Content is in GAMES CORE and D1.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
